<script setup>
import { computed, onMounted, ref } from "vue";
import { Link } from "@inertiajs/vue3";
import DocsLayout from "@/Layouts/DocsLayout.vue";
import ApplicationLogo from "@/Components/ApplicationLogo.vue";

defineOptions({ name: "DocsShow" });

const props = defineProps({
    mode: {
        type: String,
        required: true,
        validator: (value) => ["welcome", "category", "article", "not_found"].includes(value),
    },
    sidebar: {
        type: Array,
        required: true,
    },
    article: {
        type: Object,
        default: null,
    },
    category: {
        type: String,
        default: null,
    },
    initialSearch: {
        type: String,
        default: "",
    },
    meta: {
        type: Object,
        required: true,
    },
});

const searchQuery = ref(props.initialSearch ?? "");
const searchIndex = ref([]);
const indexLoaded = ref(false);

onMounted(async () => {
    if (typeof window === "undefined") {
        return;
    }

    try {
        const response = await fetch("/docs-index.json", { credentials: "same-origin" });
        if (response.ok) {
            searchIndex.value = await response.json();
        }
    } catch {
        // intentionally empty
    } finally {
        indexLoaded.value = true;
    }
});

const trimmedQuery = computed(() => searchQuery.value.trim().toLowerCase());
const isSearching = computed(() => trimmedQuery.value !== "");

const searchResults = computed(() => {
    if (!isSearching.value || searchIndex.value.length === 0) {
        return [];
    }

    const terms = trimmedQuery.value.split(/\s+/).filter((term) => term.length > 0);

    return searchIndex.value
        .map((entry) => ({ entry, score: scoreEntry(entry, terms) }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 12)
        .map(({ entry }) => entry);
});

const isActiveArticle = (slug, categoryValue) => {
    return props.article?.slug === slug && props.article?.category === categoryValue;
};

const isActiveCategory = (categoryValue) => {
    return props.category === categoryValue;
};

const formattedUpdatedAt = computed(() => {
    if (!props.article?.updated_at) {
        return null;
    }

    const date = new Date(props.article.updated_at);

    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return new Intl.DateTimeFormat("nl-NL", {
        day: "numeric",
        month: "long",
        year: "numeric",
    }).format(date);
});

function scoreEntry(entry, terms) {
    const haystacks = {
        title: (entry.title ?? "").toLowerCase(),
        headings: (entry.headings ?? []).join(" ").toLowerCase(),
        category: (entry.category_label ?? "").toLowerCase(),
        slug: (entry.slug ?? "").toLowerCase(),
    };

    let score = 0;

    for (const term of terms) {
        let termHits = 0;

        if (haystacks.title.includes(term)) {
            termHits += 8;
        }
        if (haystacks.slug.includes(term)) {
            termHits += 6;
        }
        if (haystacks.headings.includes(term)) {
            termHits += 3;
        }
        if (haystacks.category.includes(term)) {
            termHits += 1;
        }

        if (termHits === 0) {
            return 0;
        }

        score += termHits;
    }

    return score;
}

const clearSearch = () => {
    searchQuery.value = "";
};
</script>

<template>
  <DocsLayout :title="meta.title">
    <template #search>
      <div class="relative">
        <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
          <svg
            class="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M9 3.5a5.5 5.5 0 1 0 3.546 9.715l3.62 3.62a.75.75 0 1 0 1.06-1.06l-3.62-3.62A5.5 5.5 0 0 0 9 3.5ZM5 9a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z"
              clip-rule="evenodd"
            />
          </svg>
        </span>
        <input
          v-model="searchQuery"
          type="search"
          autocomplete="off"
          placeholder="Zoek in de documentatie"
          class="w-full rounded-md border-slate-300 bg-white py-1.5 pr-9 pl-9 text-sm shadow-2xs focus:border-indigo-500 focus:ring-indigo-500"
        >
        <button
          v-if="searchQuery !== ''"
          type="button"
          class="absolute inset-y-0 right-0 flex items-center pr-2 text-slate-400 hover:text-slate-600"
          aria-label="Zoekopdracht wissen"
          @click="clearSearch"
        >
          <svg
            class="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 0 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414L11.414 10l4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L8.586 10 4.293 5.707a1 1 0 0 1 0-1.414Z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>
    </template>

    <div class="mx-auto grid max-w-(--breakpoint-2xl) gap-10 px-6 py-10 lg:grid-cols-[260px_minmax(0,1fr)]">
      <aside class="lg:sticky lg:top-24 lg:self-start">
        <div
          v-if="isSearching"
          class="space-y-2"
        >
          <p class="text-xs font-semibold tracking-wide text-slate-500 uppercase">
            Zoekresultaten
          </p>
          <ul
            v-if="searchResults.length > 0"
            class="space-y-1"
          >
            <li
              v-for="result in searchResults"
              :key="result.url"
            >
              <Link
                :href="result.url"
                class="block rounded-sm px-3 py-2 text-sm text-slate-700 hover:bg-indigo-50 hover:text-indigo-700"
              >
                <span class="block font-medium">{{ result.title }}</span>
                <span class="block text-xs text-slate-500">{{ result.category_label }}</span>
              </Link>
            </li>
          </ul>
          <p
            v-else
            class="text-sm text-slate-500"
          >
            Geen resultaten voor "{{ searchQuery }}".
          </p>
        </div>

        <nav
          v-show="!isSearching"
          aria-label="Documentatie navigatie"
          class="space-y-6"
        >
          <div
            v-for="cat in sidebar"
            :key="cat.value"
          >
            <Link
              :href="cat.intro_url"
              class="flex items-center gap-2 text-xs font-semibold tracking-wide uppercase"
              :class="isActiveCategory(cat.value) ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-700'"
            >
              <ApplicationLogo :logo="cat.value" />
              {{ cat.label }}
            </Link>
            <ul
              v-if="cat.articles.length > 0"
              class="mt-2 space-y-1"
            >
              <li
                v-for="item in cat.articles"
                :key="item.url"
              >
                <Link
                  :href="item.url"
                  class="block rounded-sm px-3 py-1.5 text-sm"
                  :class="isActiveArticle(item.slug, cat.value)
                    ? 'bg-indigo-50 font-medium text-indigo-700'
                    : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'"
                >
                  {{ item.title }}
                </Link>
              </li>
            </ul>
            <p
              v-else
              class="mt-2 text-xs text-slate-400"
            >
              Nog geen artikels.
            </p>
          </div>
        </nav>
      </aside>

      <section class="min-w-0">
        <div v-if="mode === 'welcome'">
          <h1 class="text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
            Documentatie
          </h1>
          <p class="mt-4 max-w-2xl text-lg text-slate-600">
            Handleidingen en uitleg om snel met besapro aan de slag te gaan. Kies een onderwerp links of gebruik de zoekbalk bovenaan.
          </p>
          <div class="mt-10 grid gap-4 sm:grid-cols-2">
            <Link
              v-for="cat in sidebar"
              :key="cat.value"
              :href="cat.intro_url"
              class="rounded-2xl border border-slate-200 bg-white p-6 transition hover:border-indigo-300 hover:shadow-xs"
            >
              <h2 class="flex items-center gap-2 text-lg font-semibold text-slate-900">
                <ApplicationLogo :logo="cat.value" />
                <span>{{ cat.label }}</span>
              </h2>
              <p class="mt-2 text-sm text-slate-600">
                {{ cat.articles.length }} {{ cat.articles.length === 1 ? "artikel" : "artikels" }}
              </p>
            </Link>
          </div>
        </div>

        <article
          v-else-if="(mode === 'article' || mode === 'category') && article"
          class="prose prose-slate max-w-none"
        >
          <header class="not-prose mb-8">
            <p
              v-if="article.category_label"
              class="text-sm font-semibold text-indigo-600"
            >
              {{ article.category_label }}
            </p>
            <h1 class="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              {{ article.title }}
            </h1>
            <p
              v-if="formattedUpdatedAt"
              class="mt-4 text-xs text-slate-500"
            >
              Bijgewerkt op {{ formattedUpdatedAt }}
            </p>
          </header>

          <!-- eslint-disable-next-line vue/no-v-html -->
          <div v-html="article.html" />
        </article>

        <div v-else-if="mode === 'category' && !article">
          <h1 class="text-3xl font-semibold tracking-tight text-slate-900">
            Nog geen inhoud
          </h1>
          <p class="mt-3 max-w-2xl text-base text-slate-600">
            Voor deze categorie zijn nog geen artikels beschikbaar. Kom binnenkort terug of gebruik de zoekbalk om verwante onderwerpen te vinden.
          </p>
        </div>

        <div v-else-if="mode === 'not_found'">
          <h1 class="text-3xl font-semibold tracking-tight text-slate-900">
            Niet gevonden
          </h1>
          <p class="mt-3 max-w-2xl text-base text-slate-600">
            Het document <span class="font-medium text-slate-900">"{{ initialSearch }}"</span> bestaat niet (meer). We hebben automatisch gezocht op deze tekst, kies hieronder of links in de zijbalk een resultaat.
          </p>
          <div
            v-if="searchResults.length > 0"
            class="mt-8"
          >
            <h2 class="text-sm font-semibold tracking-wide text-slate-500 uppercase">
              Zoekresultaten
            </h2>
            <ul class="mt-3 space-y-3">
              <li
                v-for="result in searchResults"
                :key="result.url"
              >
                <Link
                  :href="result.url"
                  class="block rounded-xl border border-slate-200 bg-white p-4 hover:border-indigo-300 hover:shadow-xs"
                >
                  <p class="text-base font-semibold text-slate-900">
                    {{ result.title }}
                  </p>
                  <p class="mt-2 text-xs text-slate-500">
                    {{ result.category_label }}
                  </p>
                </Link>
              </li>
            </ul>
          </div>
          <p
            v-else-if="indexLoaded"
            class="mt-8 text-sm text-slate-500"
          >
            Geen resultaten gevonden. Probeer een andere zoekopdracht of bekijk een categorie links.
          </p>
        </div>
      </section>
    </div>
  </DocsLayout>
</template>

<style scoped>
.prose :deep(img) {
  max-width: 42rem;
  margin-left: auto;
  margin-right: auto;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.1);
}

.prose :deep(img.doc-img-full) {
  max-width: 100%;
}
</style>
